import { useEffect, useState } from 'preact/hooks';
import { type Session, describeError } from '../api/client';
import { listReflections, REFLECTION_PAGE_SIZE } from '../api/reflections';
import { displayDate } from '../api/adapter';
import type { WireReflection } from '../api/wire';

import { Pressable } from '../components/a11y/Pressable';
import { ListRow } from '../components/navigation/ListRow.jsx';

/** Everything written, newest first.
 *
 *  The day view could always reach a reflection, but only by knowing its date
 *  and paging to it — which answers the wrong question. A review asks "what
 *  have I been writing", and that needs the reflections themselves as the list.
 *
 *  Tapping one opens the ordinary editor at that date, so editing, photographs
 *  and conflict handling are the same here as on TODAY rather than a second
 *  implementation that would drift.
 *
 *  Appends rather than replaces on paging: this is a reading surface, and
 *  losing your place to see the next twenty is the one thing it must not do.
 */
export function Reflections({
  session,
  onOpen,
  onBack,
}: {
  session: Session;
  /** Opens the reflection editor at this date. */
  onOpen: (journalDate: string) => void;
  onBack: () => void;
}) {
  const [items, setItems] = useState<WireReflection[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await listReflections(session);
        if (cancelled) return;
        setItems(page.items);
        setCursor(page.nextCursor);
        setError(null);
      } catch (caught) {
        if (!cancelled) setError(describeError(caught));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  async function more() {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await listReflections(session, cursor);
      setItems((current) => [...(current ?? []), ...page.items]);
      setCursor(page.nextCursor);
      setError(null);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <main style={{ padding: 'var(--bujo-gutter)', maxWidth: 560, margin: '0 auto' }}>
      <header style={header}>
        <div style={eyebrow}>BUJO / REVIEW</div>
        <h1 style={h1}>REFLECTIONS</h1>
        <div style={subtitle}>
          {items === null
            ? 'reading…'
            : items.length === 0
              ? 'nothing written yet'
              : `${items.length} shown${cursor ? ' · more below' : ''}`}
        </div>
      </header>

      {error ? <p style={errorText}>{error}</p> : null}

      {items !== null && items.length === 0 && !error ? (
        <p style={emptyText}>
          Reflections written on the daily log appear here, newest first.
        </p>
      ) : null}

      {(items ?? []).map((reflection) => (
        <Pressable
          key={reflection.journal_date}
          label={`Reflection for ${displayDate(reflection.journal_date)}`}
          onPress={() => onOpen(reflection.journal_date)}
        >
          <ListRow
            label={displayDate(reflection.journal_date)}
            meta={preview(reflection.content)}
            dim={reflection.status === 'draft'}
          />
        </Pressable>
      ))}

      {/* Only when there is another page. A dead `LOAD MORE` at the end of a
          list is a worse answer than the list simply ending. */}
      {cursor ? (
        <button type="button" onClick={() => void more()} disabled={loadingMore} style={loadMore}>
          {loadingMore ? 'READING…' : `LOAD ${REFLECTION_PAGE_SIZE} MORE`}
        </button>
      ) : null}

      <button type="button" onClick={onBack} style={back}>
        ◀ BACK TO INDEX
      </button>
    </main>
  );
}

/** The first line, short enough to sit in the meta column.
 *
 *  Newlines collapse to a space: a reflection that opens with a short line
 *  would otherwise preview as one word and tell you nothing.
 */
function preview(content: string): string {
  const flat = content.replace(/\s+/g, ' ').trim();
  if (!flat) return 'empty';
  return flat.length > 48 ? `${flat.slice(0, 47)}…` : flat;
}

const header = {
  paddingBottom: 'var(--bujo-space-2)',
  marginBottom: 'var(--bujo-space-4)',
  borderBottom: '1px solid var(--bujo-rule-heavy)',
};
const eyebrow = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-eyebrow)',
  color: 'var(--bujo-text-meta)',
  paddingBottom: 'var(--bujo-space-2)',
};
const h1 = {
  margin: 0,
  font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1.1 var(--bujo-font-mono)',
  color: 'var(--bujo-accent)',
};
const subtitle = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
  paddingTop: 'var(--bujo-space-2)',
};
const emptyText = {
  margin: 0,
  font: 'var(--bujo-fw-regular) var(--bujo-fs-body)/var(--bujo-lh-prose) var(--bujo-font-sans)',
  color: 'var(--bujo-text-meta)',
};
const errorText = {
  margin: 0,
  font: 'var(--bujo-fw-regular) var(--bujo-fs-body)/var(--bujo-lh-prose) var(--bujo-font-sans)',
  color: 'var(--bujo-state-error)',
};
const loadMore = {
  marginTop: 'var(--bujo-space-3)',
  minHeight: 'var(--bujo-tap-min)',
  width: '100%',
  border: '1px solid var(--bujo-rule-hairline)',
  color: 'var(--bujo-text-meta)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  cursor: 'pointer',
};
const back = {
  marginTop: 'var(--bujo-space-4)',
  minHeight: 'var(--bujo-row-min)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-badge)',
  color: 'var(--bujo-accent)',
  cursor: 'pointer',
};
